import { WButton } from "@/components/button/WButton.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { NodeResizeButton } from "@/modules/services/kubernetes/NodeResizeModal.tsx";
import { updateServicePropertiesMutation } from "@/modules/services/servicesQuery.ts";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { KubernetesProviderWorker, ManagedKubernetesService } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";

export function InfrastructureContent({ item }: { item: ManagedKubernetesService }) {
    const { t } = useLingui();
    const { properties, uuid } = item;

    const mutation = useStandardMutation(updateServicePropertiesMutation);

    async function onDelete(index: number) {
        const providerWorkers = properties?.spec?.provider?.workers ?? [];
        const workers = [...providerWorkers];
        if (Number.isInteger(index)) {
            workers.splice(index, 1);
        }

        const provider = {
            workers: [...workers],
        };

        const maintenance = {
            ...notNull(properties?.spec?.maintenance),
        };

        await mutation.mutateAsync({ uuid: uuid, body: { maintenance, provider } });
    }

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Infrastructure</Trans>} />

            <WTable
                afterTable={
                    <NodeResizeButton
                        service={item}
                        button={
                            <WButton color="primary" size="bar" variant="border" icon="jp-icon-add" action={undefined}>
                                <Trans>Add node pool</Trans>
                            </WButton>
                        }
                    />
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>Node pool</Trans>
                        </th>
                        <th>
                            <Trans>Machine type</Trans>
                        </th>
                        <th>
                            <Trans>Disk</Trans>
                        </th>
                        <th>
                            <Trans>Autoscaler min.</Trans>
                        </th>
                        <th>
                            <Trans>Autoscaler max.</Trans>
                        </th>
                        <th>
                            <Trans>Max. surge</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {properties?.spec?.provider.workers.map((node: KubernetesProviderWorker, index: number) => (
                        <tr key={index}>
                            <td>
                                <div className="flex items-center gap-0.375em">
                                    <MaskIcon className="size-0.75rem jp-container-icon text-primary" />
                                    {node.name}
                                </div>
                            </td>
                            <td>
                                {node.machine.vcpu} <Trans>vCPU</Trans> / {node.machine.ram} <Trans>GB</Trans>
                            </td>
                            <td>
                                {node?.volume?.size ? parseInt(node.volume.size, 10) : 0} <Trans>GB</Trans>
                            </td>
                            <td>
                                <Trans>
                                    {node.minimum} <Plural value={node.minimum} one="node" other="nodes" />
                                </Trans>
                            </td>
                            <td>
                                <Trans>
                                    {node.maximum} <Plural value={node.maximum} one="node" other="nodes" />
                                </Trans>
                            </td>
                            <td>
                                <Trans>
                                    {node.maxSurge} <Plural value={node.maxSurge} one="node" other="nodes" />
                                </Trans>
                            </td>
                            <td className="text-right">
                                <div className="flex gap-0.5em justify-end">
                                    <NodeResizeButton
                                        node={node}
                                        service={item}
                                        index={index}
                                        button={<WModalButton inTable label={t`Edit`} icon="jp-icon-edit" />}
                                    />

                                    <DeleteModal
                                        isDisabled={notNull(properties?.spec?.provider.workers).length < 2}
                                        inTable
                                        modalAction={() => onDelete(index)}
                                    >
                                        TODO
                                    </DeleteModal>
                                </div>
                            </td>
                        </tr>
                    ))}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}
